import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import type { UserPermissions } from "common/types";

import ContentContainer from "./ContentContainer";
import ContentHeader from "./ContentHeader";
import ContentScroll from "./ContentScroll";

import type { JSX, ReactNode } from "react";

export default function NavTabsContentContainer({
  children,
  desktopActionComponent,
  tabItems,
}: {
  children: ReactNode;
  desktopActionComponent: JSX.Element;
  tabItems: {
    label: string;
    path: string;
    visibleForPermissions?: UserPermissions;
  }[];
}) {
  const { pathname } = useLocation();

  const activeTabItem = tabItems.find(
    ({ path }) => path && pathname.split("/").includes(path),
  );

  return (
    <ContentContainer className="relative">
      <ContentHeader>
        <div className="flex flex-col grow">
          <div className="flex gap-x-1 max-lg:w-full">
            {tabItems.map(({ label, path, visibleForPermissions }) => {
              const isActive = activeTabItem
                ? activeTabItem.path === path
                : path === "";

              return (
                <RestrictedComponent
                  key={path}
                  visibleForPermissions={visibleForPermissions}
                >
                  <Link
                    aria-current={isActive ? "page" : undefined}
                    to={path ? `../${path}` : ".."}
                    className={clsx(
                      "z-10 max-lg:flex-1 justify-center px-2 flex pt-4 pb-3.75 border-b-2 h-full items-center transition-[color] hover:text-black-950",
                      isActive
                        ? "text-black-950 text-md-bold"
                        : "text-black-400 border-b-transparent text-button-md",
                    )}
                  >
                    {label}
                  </Link>
                </RestrictedComponent>
              );
            })}
          </div>
          <div className="flex h-px w-full lg:hidden bg-black-200 -mt-px" />
        </div>

        <RenderIf condition={!!desktopActionComponent}>
          <div className="flex gap-x-2 max-lg:hidden">
            {desktopActionComponent}
          </div>
        </RenderIf>
      </ContentHeader>

      <ContentScroll className="p-4 gap-y-4">{children}</ContentScroll>
    </ContentContainer>
  );
}
